<template>
  <div class="utilities">
    <div class="utilities__buttons">
      <slot name="utilities-slot" />
    </div>
    <button class="dropdown-toggle-all" @click="toggleDropdownAll"><span>{{ openedDropdowns.length > 0 ? 'Collapse All' : 'Expand All' }}</span></button>
  </div>
  <div class="json-raw-viewer scrollbar">
    <v-expansion-panels v-model="openedDropdowns" multiple>
      <v-expansion-panel v-if="imageObj" value="image" class="panel">
        <v-expansion-panel-title class="panel__title" title="Image">
          <div class="panel__title-text">
            <span>Image</span>
            <!-- <IconButton
              v-model="internalShowTypes['image']"
              aria-label="Toggle Visibility"
              title="Toggle Visibility"
              class="ms-2"
              :icon="'visibility_off'"
              :iconActive="'visibility'"
              :width="21"
              :height="21"
              :type="'toggle-icon'"
              :showHover="true"
            /> -->
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="panel__text">
          <div class="row panel__item">
            <div class="col-12 settings__control-group">
              <ImageDetails :imageObj="imageObj" />
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel value="annotation-sets" class="panel">
        <v-expansion-panel-title class="panel__title" title="Annotation Sets">
          <div class="panel__title-text">
            <span>Annotation Sets</span>
            <div class="icons">
              <SVGIcon
                v-if="isEditing"
                :iconName="'add'"
                :width="'21px'"
                :height="'21px'"
                class="radio-icon active-icon"
                @click.stop="addAnnotationSets"
              />
              <label for="set" class="panel__radio-label" title="Color Annotations by Sets">
                <input
                  id="set"
                  v-model="displayColorBy"
                  type="radio"
                  value="set"
                  class="color-radio set icon"
                  @click.stop=""
                  @change="handleDisplayColorByChanged"
                >
                <SVGIcon
                  v-if="displayColorBy === 'set'"
                  :iconName="'invert_colors'"
                  :width="'21px'"
                  :height="'21px'"
                  class="radio-icon"
                  :class="{ 'active-icon': displayColorBy==='set' }"
                  @click.stop=""
                />
                <SVGIcon
                  v-else
                  :iconName="'invert_colors_off'"
                  :width="'21px'"
                  :height="'21px'"
                  @click.stop=""
                />
              </label>
            </div>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="panel__text chkbox-list">
          <template v-if="annotationSets && annotationSets.length > 0">
            <v-checkbox
              v-for="s in annotationSets"
              :key="`set-checkbox-${s.id}`"
              v-model="internalSelectedAnnotationSets"
              :value="s['id']"
              :hide-details="true"
              :disabled="fixedAnnotationSet"
              density="compact"
              class="chkbx-li"
              @change="handleSelectedAnnotationSetsChanged"
            >
              <template #label>
                <div class="annset-legend-color" :style="{ background: annotationSetColorMap[s.id] }" />
                <div class="check-box-text">
                  {{ s['name'] }}
                </div>
              </template>
              <template #append>
                <IconButton
                  aria-label="Toggle Visibility"
                  title="Edit Annotations"
                  :icon="'edit'"
                  :width="22"
                  :height="22"
                  :type="'click'"
                  :showHover="true"
                  @click="handleEditAnnotations(s)"
                />
              </template>
            </v-checkbox>
          </template>
          <template v-else>
            <span class="empty-state">No annotation set</span>
          </template>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel value="labels" class="panel">
        <v-expansion-panel-title class="panel__title" title="Labels">
          <div class="panel__title-text">
            <span>Labels</span>
            <div class="icons">
              <SVGIcon
                v-if="isEditing"
                :iconName="'add'"
                :width="'21px'"
                :height="'21px'"
                class="radio-icon active-icon"
                @click.stop="addLabels"
              />
              <label for="label" class="panel__radio-label" title="Color Annotations by Labels">
                <input
                  id="label"
                  v-model="displayColorBy"
                  type="radio"
                  value="label"
                  class="color-radio label icon"
                  @click.stop=""
                  @change="handleDisplayColorByChanged"
                >
                <SVGIcon
                  v-if="displayColorBy==='label'"
                  :iconName="'invert_colors'"
                  :width="'21px'"
                  :height="'21px'"
                  class="radio-icon"
                  :class="{ 'active-icon':displayColorBy==='label' }"
                  @click.stop=""
                />
                <SVGIcon
                  v-else
                  :iconName="'invert_colors_off'"
                  :width="'21px'"
                  :height="'21px'"
                  class="radio-icon"
                  @click.stop=""
                />
              </label>
            </div>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="panel__text label-list-wrapper scrollbar">
          <template v-if="labels &&labels.length > 0">
            <ul class="label-list">
              <li
                v-for="label in labels"
                :key="`label-checkbox-${label.id}`"
                class="chkbox-flex-row"
                :class="{_active: selectedLabelForAudit && selectedLabelForAudit.id === label.id && isEditing,
                         _hover: true && isEditing,
                         _pointer: isEditing}"
                @click="handleLabelListItemClicked(label)"
              >
                <v-checkbox
                  v-model="internalSelectedLabels"
                  :value="label.index"
                  :hide-details="true"
                  density="compact"
                  class="chkbx-li label-chkbox"
                  @click.stop
                  @change="handleSelectedLabelsChanged"
                />
                <div class="label-color-name">
                  <ColorPicker
                    :color="getColor(label)"
                    :appendToBody="true"
                    :width="16"
                    :height="16"
                    :radius="0"
                    :offset="[-10, 20]"
                    @new-color="(color) => stopEditingColor(i, label, color)"
                  />
                  <span class="check-box-text">{{ label.name }}</span>
                </div>
              </li>
            </ul>
            <span class="text-instruction">Click on a label to select it.</span>
          </template>
          <template v-else>
            <span class="text-instruction"><span class="hyperlink" @click="addLabels">Add label(s)</span> to start editing.</span>
          </template>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel value="topics" class="panel">
        <v-expansion-panel-title class="panel__title" title="Topics">
          <div class="panel__title-text">
            <span>Topics {{ listedTopics && listedTopics.length > 0 ? `(${listedTopics.length})` : '' }}</span>
            <BaseMenu
              v-if="!hideAddTopics"
              :placement="'bottom-start'"
              :offset="[0,0]"
              :appendToBody="true"
              trigger="hover"
            >
              <template #menu>
                <div class="topic-selector-text scrollbar">
                  <div class="chkbox-list">
                    <v-checkbox
                      v-for="s in ALL_TYPES"
                      :key="s.key"
                      v-model="selectedTopics"
                      :value="s.key"
                      :hide-details="true"
                      density="compact"
                      class="chkbx-li"
                      :disabled="groupedAnnotations && Object.keys(groupedAnnotations).includes(s.key)"
                    >
                      <template #label>
                        <div class="check-box-text">
                          {{ s.display }}
                        </div>
                      </template>
                    </v-checkbox>
                  </div>
                </div>
              </template>
              <SVGIcon
                v-if="isEditing"
                :iconName="'add'"
                class="topic-selector-icon"
                :width="'22px'"
                :height="'22px'"
                @click.stop=""
              />
            </BaseMenu>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="panel__text nowrap">
          <v-expansion-panels v-model="openedTopics" multiple>
            <template v-if="listedTopics && listedTopics.length > 0">
              <v-expansion-panel
                v-for="topic in listedTopics"
                :key="topic.key"
                class="panel"
                :value="topic.key"
              >
                <v-expansion-panel-title class="panel__title" :title="topic.display">
                  <div class="panel__title-text topic-title">
                    <span>{{ topic.display }}</span>
                    <div v-if="!topic.unknown">
                      <IconButton
                        v-if="displayProjection && topic.key in displayProjection"
                        :modelValue="displayProjection[topic.key]"
                        aria-label="Toggle 2D Projection"
                        title="Toggle 2D Projection"
                        class="ms-2"
                        :icon="'screen_share'"
                        :iconActive="'screen_share_filled'"
                        :width="21"
                        :height="21"
                        :type="'toggle-icon'"
                        :showHover="true"
                        @click="toggleDisplayProjection(topic.key)"
                      />
                      <IconButton
                        v-if="displayLabelTags && topic.key in displayLabelTags && internalShowTypes[topic.key]"
                        :modelValue="displayLabelTags[topic.key]"
                        aria-label="Toggle Display Labels"
                        title="Toggle Display Labels"
                        class="ms-2"
                        :icon="'tag'"
                        :iconActive="'tag_filled'"
                        :width="21"
                        :height="21"
                        :type="'toggle-icon'"
                        :showHover="true"
                        @click="toggleDisplayLabelTags(topic.key)"
                      />
                      <IconButton
                        v-model="internalShowTypes[topic.key]"
                        aria-label="Toggle Visibility"
                        title="Toggle Visibility"
                        class="ms-2"
                        :icon="'visibility_off'"
                        :iconActive="'visibility'"
                        :width="21"
                        :height="21"
                        :type="'toggle-icon'"
                        :showHover="true"
                        @click="handleShowTypesChanged"
                      />
                    </div>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text class="panel__text">
                  <div
                    v-for="annotation in groupedAnnotations[topic.key]"
                    :key="annotation.id"
                    class="row panel__item"
                  >
                    <div class="col-12 settings__control-group">
                      <template v-if="annotation.type==='pcd' || annotation.type==='radar' || annotation.type==='mask'">
                        <div class="raw-text">
                          <JsonView
                            :data="{ 'raw_file': annotation['raw'] }"
                            :rootKey="`${annotation.type}_${annotation.id}`"
                            :itemId="annotation.id"
                            :loading="loadingRaw"
                            :selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
                            :isEditing="isEditing"
                            @open-in-full="openInFull(annotation)"
                            @opened="handleAnnotationOpened(annotation)"
                          />
                        </div>
                      </template>
                      <template v-else-if="annotation.type==='box' || annotation.type==='seg'">
                        <div class="json-tree">
                          <JsonView
                            :data="formatAnnotation(annotation)"
                            :rootKey="annotation.label_name ? `${annotation.label_name}`: `${annotation.label_index}`"
                            :itemId="annotation.id"
                            :selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
                            :removable="true"
                            :isEditing="isEditing"
                            @selected="handleSelect(annotation)"
                            @remove-annotation="handleDeleteAnnotation"
                          />
                        </div>
                      </template>
                      <template v-else-if="annotation.type==='3dbox'">
                        <div class="json-tree">
                          <JsonView
                            :data="formatDataJson(annotation)"
                            :rootKey="annotation.label_name ? `${annotation.label_name}`: `${annotation.label_index}`"
                            :itemId="annotation.id"
                            :selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
                            :removable="true"
                            :isEditing="isEditing"
                            @selected="handleSelect(annotation)"
                            @remove-annotation="handleDeleteAnnotation"
                          />
                        </div>
                      </template>
                      <template v-else>
                        <div class="json-tree">
                          <JsonView
                            :data="formatDataJson(annotation)"
                            :rootKey="`${annotation.type}_${annotation.id}`"
                            :itemId="annotation.id"
                            :selectedAnnotationIdentifiers="selectedAnnotationIdentifiers"
                            :isEditing="isEditing"
                            @selected="handleSelect(annotation)"
                          />
                        </div>
                      </template>
                    </div>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </template>
            <div v-else>
              <span class="text-instruction">Annotations and sensors will show here.</span>
            </div>
          </v-expansion-panels>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup>
import JsonView from '@/components/JSONViewer/JsonView.vue';
import {
  ref, computed, onMounted, watch, toRefs,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import IconButton from '@/components/IconButton.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import useAnnotationColorMap from '@/composables/useAnnotationColorMap.js';
import ImageDetails from '@/components/DatasetComponent/AnnotationTool/ImageDetails.vue';
import useAnnotationType from '@/composables/useAnnotationType.js';
import BaseMenu from '@/components/BaseMenu.vue';
import ColorPicker from '@/components/ColorPicker.vue';

const props = defineProps({
  imageObj: { type: Object, default: null },
  labels: { type: Array, default: () => [] },
  annotationSets: { type: Array, default: () => [] },
  selectedAnnotationSets: { type: Array, default: null },
  fixedAnnotationSet: { type: Boolean, default: false },
  displayLabelTags: { type: Object, default: null },
  displayProjection: { type: Object, default: null },
  colorAnnotationsBy: { type: Object, default: () => ({ type: 'label' }) },
  groupedAnnotations: { type: Object, default: () => {} },
  hideAddTopics: { type: Boolean, default: false },
  selectedLabels: { type: Array, default: () => [] },
  selectedLabel: { type: Object, default: () => {} },
  selectedAnnotationIdentifiers: { type: Array, default: () => [] },
  isEditing: { type: Boolean, default: true },
  showTypes: { type: Object, default: () => {} },
});

const {
  imageObj,
  labels,
  annotationSets,
  fixedAnnotationSet,
  groupedAnnotations,
  hideAddTopics,
  selectedAnnotationIdentifiers,
  isEditing,
} = toRefs(props);

const {
  ALL_TYPES,
  selectedDisplayTypes,
} = useAnnotationType();

const selectedTopics = ref([]);
const internalSelectedAnnotationSets = ref([]);
const internalSelectedLabels = ref([]);
const internalShowTypes = ref({});

const emit = defineEmits([
  'update:displayLabelTags',
  'update:displayProjection',
  'update:selectedAnnotationSets',
  'open-in-full',
  'update:selectedLabels',
  'update:selectedLabel',
  'update:colorAnnotationsBy',
  'edit-for-annotation-set',
  'select-annotations',
  'new-color',
  'remove-annotation',
  'update:showTypes',
  'add-annotation-sets',
  'add-labels',
]);

const dataConnect = new DatastoreConnect();

const openedDropdowns = ref(['topics', 'labels']);

const openedTopics = ref([]);

const displayColorBy = ref('label');

const loadingRaw = ref(false);

const listedTopics = computed(() => {
  const allTypeKeys = ALL_TYPES.map((at) => at.key);
  const unknownTypes = selectedTopics.value.filter((t) => !allTypeKeys.includes(t)).map((t) => ({ key: t, display: t, unknown: true }));
  return [...ALL_TYPES.filter((e) => selectedTopics.value.includes(e.key)), ...unknownTypes];
});

const selectedLabelForAudit = ref(labels.value ? labels.value[0] : {});

watch(groupedAnnotations, () => {
  if (groupedAnnotations.value) {
    selectedTopics.value = Object.keys(groupedAnnotations.value);
  }
}, { deep: true, immediate: true });

watch(listedTopics, () => {
  if (listedTopics.value) {
    selectedDisplayTypes.value = listedTopics.value;
  }
}, { immediate: true });

watch(() => props.selectedAnnotationSets, (newVal) => {
  internalSelectedAnnotationSets.value = newVal;
});

watch(() => props.selectedLabels, (newVal) => {
  internalSelectedLabels.value = newVal;
});

watch(() => props.colorAnnotationsBy, () => {
  displayColorBy.value = props.colorAnnotationsBy.type;
});

watch(() => props.showTypes, () => {
  internalShowTypes.value = props.showTypes;
});

watch(labels, () => {
  if (labels.value.length > 0) {
    // if the selected label for audit's identifier is not in the label list
    if (selectedLabelForAudit.value) {
      const isSelectedLabelInList = labels.value.find((e) => e.id === selectedLabelForAudit.value.id);
      if (!isSelectedLabelInList) {
        selectedLabelForAudit.value = labels.value[0];
        if (internalSelectedLabels.value.length === 0) {
          internalSelectedLabels.value = labels.value.map((e) => e.index);
        }
      }
    } else {
      selectedLabelForAudit.value = labels.value[0];
      if (internalSelectedLabels.value.length === 0) {
        internalSelectedLabels.value = labels.value.map((e) => e.index);
      }
    }
  }
}, { deep: true });

function handleSelectedAnnotationSetsChanged() {
  emit('update:selectedAnnotationSets', internalSelectedAnnotationSets.value);
}

function handleSelectedLabelsChanged() {
  emit('update:selectedLabels', internalSelectedLabels.value);
}

function handleDisplayColorByChanged() {
  emit('update:colorAnnotationsBy', { type: displayColorBy.value });
}

function handleShowTypesChanged() {
  emit('update:showTypes', internalShowTypes.value);
}

// Setup annotation color maps
const { map: labelColorMap } = useAnnotationColorMap({ items: labels, key: 'index' });
const { map: annotationSetColorMap } = useAnnotationColorMap({ items: annotationSets, key: 'id' });

function stopEditingColor(i, label, color) {
  if (!color || color === '') {
    return;
  }

  const params = {
    label_id: label.id,
    label_index: label.index,
    label_name: label.name,
    label_color: color,
  };

  emit('new-color', params);
}

function formatAnnotation(annotation) {
  const label = labels.value.find((e) => e.id === annotation.label_id);
  const result = {};
  if (annotation.type === "seg") {
    result.segments = annotation.polygon;
    if (label) {
      result.label_name = label.name;
      result.label_index = label.index;
    } else {
      result.label_name = "";
      result.label_index = 0;
    }
  } else if (annotation.type === "box") {
    result.segments = annotation.polygon;
    result.x = annotation.x;
    result.y = annotation.y;
    result.w = annotation.w;
    result.h = annotation.h;
    result.score = annotation.score;
    result['size_%'] = annotation.w * annotation.h;
    result['size_px'] = (annotation.w * annotation.h) * imageObj.value.width * imageObj.value.height;
    if (label) {
      result.label_name = label.name;
      result.label_index = label.index;
    } else {
      result.label_name = "";
      result.label_index = 0;
    }
  }
  return result;
}

function formatDataJson(annotation) {
  const label = labels.value.find((e) => e.id === annotation.label_id);
  let result = {};
  if (annotation.type === "3dbox") {
    result.x = annotation["data_json"].x;
    result.y = annotation["data_json"].y;
    result.z = annotation["data_json"].z;
    result.dx = annotation["data_json"].dx;
    result.dy = annotation["data_json"].dy;
    result.dz = annotation["data_json"].dz;
    if (label) {
      result.label_name = label.name;
      result.label_index = label.index;
    } else {
      result.label_name = "";
      result.label_index = 0;
    }
  } else {
    result = annotation['data_json'];
  }
  return result;
}

async function readFile(annotation) {
  loadingRaw.value = true;
  let data;
  if (annotation.is_sensor) {
    data = await dataConnect.getSensorFile(annotation.id)
      .catch((error) => {
        console.log(error);
        return '';
      });
  } else {
    data = await dataConnect.getAnnotationFile(annotation.id)
      .catch((error) => {
        console.log(error);
        return '';
      });
  }
  loadingRaw.value = false;
  return data;
}

async function handleAnnotationOpened(annotation) {
  if ((annotation.data_url || annotation.url) && !annotation.raw) {
    annotation.raw = await readFile(annotation);
  } else if (annotation.polygon && !annotation.raw) {
    annotation.raw = annotation.polygon;
  }
}

function handleSelect(annotation) {
  emit('select-annotations', annotation.id);
}

function handleLabelListItemClicked(label) {
  if (!isEditing.value) return;

  if (selectedLabelForAudit.value !== label) {
    selectedLabelForAudit.value = label;
    emit('update:selectedLabel', label);
  }
}

onMounted(() => {
  if (props.selectedAnnotationSets) {
    internalSelectedAnnotationSets.value = props.selectedAnnotationSets;
  }
  if (props.selectedLabels) {
    internalSelectedLabels.value = props.selectedLabels;
  }
  if (props.colorAnnotationsBy) {
    displayColorBy.value = props.colorAnnotationsBy.type;
  }
  if (props.showTypes) {
    internalShowTypes.value = props.showTypes;
  }
});

function handleEditAnnotations(annotationSet) {
  emit('edit-for-annotation-set', annotationSet);
}

function handleDeleteAnnotation(annotation) {
  if (!isEditing.value) return;

  emit('remove-annotation', annotation);
}

function getColor(label) {
  let color = "";
  color = label.color ? label.color : labelColorMap.value[label.index];
  return color;
}

function toggleDropdownAll() {
  if (openedDropdowns.value.length === 0) {
    openedDropdowns.value = ['image', 'annotation-sets', 'labels', 'topics'];
  } else {
    openedDropdowns.value = [];
  }
}

function toggleDisplayLabelTags(key) {
  emit('update:displayLabelTags', { ...props.displayLabelTags, [key]: !props.displayLabelTags[key] });
}

function toggleDisplayProjection(key) {
  emit('update:displayProjection', { ...props.displayProjection, [key]: !props.displayProjection[key] });
}

function addAnnotationSets() {
  emit('add-annotation-sets');
}

function addLabels() {
  emit('add-labels');
}

</script>

<style lang="scss" scoped>

.dropdown-toggle-all {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  span {
    // margin: 5px 15px 0 5px;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-primary);
    cursor: pointer;

    &:hover {
      color: rgb(147, 146, 146);
    }
  }
}

.json-raw-viewer {
  display: flex;
  width: 100%;
  overflow-y: auto;
}
.panel {
  padding: 0 !important;
  border-radius: 0;

  &__title {
    height: 30px;
    min-height: 0;
    padding: 5px 5px 5px 15px;
    font-weight: 600;
    border-radius: 0;
    border-bottom: 0.5px solid rgb(132, 130, 130);
  }

  &__title-text {
    display: flex;
    width: 100%;
    margin: 0 10px 0 0;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .icons {
      display: flex;
      flex-direction: row;
      gap: 5px;
    }
  }

  .topic-title {
    margin-left: 10px;
  }

  &__text {
    background: none;
    // border-bottom: 0.5px solid rgb(132, 130, 130);
    // overflow-y: auto;
    height: fit-content;
  }

  &__item {
    display: flex;
    flex-direction: row;
  }

  &__item > * {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }

  &__radio-label {
    width: 21px;
    height:21px;

    .icon {
      cursor: pointer;
    }

    .icon:hover {
      @include themify() {
        background-color: themed('icon-hover-color');
        border-radius: 100%;
        box-shadow: 0 0 0 4px themed('icon-hover-color');
      }
    }
  }

  :deep() {
    .v-expansion-panel-title--active {
      min-height: 0;
    }
  }
}

.topic-selector-icon {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.topic-selector-text {
  min-width: 180px;
  width:fit-content;
  min-height: 200px;
  max-height: 400px;
  padding-right: 10px;
  overflow-y: auto;
}

.raw-text {
  width: 100%;
  text-align: left;
  font-size: 0.9rem;
}

.json-tree {
  width: 100%;
  text-align: left;
  font-size: 0.9rem;
}
pre{
  width: 100%;
  overflow-x: scroll;
}

.label-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 50%;
  margin: 0;
  list-style-type: none;

  li {
    user-select: none;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -o-user-select: none;
    flex-direction: row;
    display: flex;
    align-items: center;
    margin-bottom: unset;
    height: 30px;
    padding: 4px 8px;
    font-size: 0.9rem;
    font-weight: 600;
    border: 1px solid transparent;
    border-left: 4px solid transparent;

    button {
      @include themify() {
        color: themed('color-primary');
      }
    }

    span {
      margin-right: auto;
    }

    &._active {
      background: rgba(140,140,140,0.2);
      cursor: pointer;

      @include themify() {
        border: 1px solid black;
        border-left: 4px solid themed('color-primary');
      }
    }
  }

  li+li {
    margin-top: 5px;
  }

  li._hover {
    &:hover {
      background: rgba(140,140,140,0.2);
      cursor: pointer;
    }
  }

  li._pointer {
    cursor: pointer;
  }

  &__symbol {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  &__box-symbol {
    border: 2px solid;
  }

  .chkbox-flex-row {
    display: flex;
    flex-direction: row;
    align-items: center;

    .label-color-name{
      display: flex;
      flex-direction: row;
      align-items: center;

      span {
        margin-left: 10px;
      }
    }
  }
}

.check-box-text {
    font-size: 0.9rem;
    color: #181a1f;
    font-weight: 600;
    letter-spacing: 0;
  }

.label-chkbox {
    width: fit-content;
  }

.annset-legend-color {
  min-width: 16px;
  min-height: 16px;
  margin-right: 8px;
  border-radius: 100%;
  border: 0.1px solid grey;
}

:deep() {
  .v-label {
      opacity: 1!important;
    }

  .v-expansion-panel--active {
      margin-top: 0;
    }

  .v-expansion-panel--after-active {
    margin-top: 0;
  }
  .v-selection-control__wrapper {
    margin-right: 5px;
  }

  .v-expansion-panel-text__wrapper {
    padding: 8px 12px 0 0 !important;
  }

  .nowrap>.v-expansion-panel-text__wrapper {
    padding: 0
  }

  .label-list-wrapper {
    overflow-y: auto;
  }

  .label-list-wrapper>.v-expansion-panel-text__wrapper {
    max-height: 300px;
    height: fit-content;
  }

}

.color-radio {
  position: absolute;
  opacity: 0;
  width: 30px;
  height: 30px;
}

.radio-icon:hover,
.radio-icon:focus-visible {
  @include themify() {
    background-color: themed('icon-hover-color');
    border-radius: 100%;
    box-shadow: 0 0 0 4px themed('icon-hover-color');
  }
}

.active-icon {
  @include themify() {
    color: themed('icon-color-primary');
    border-radius: 100%;
  }
}

.utilities {
  display: flex;
  flex-direction: row;
  height: 30px;
  margin: 4px 6px;
  gap: 4px;

  &__buttons {
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    align-items: center;
  }
}

.empty-state {
  font-weight: 600;
  font-size: 0.9rem;
}

.text-instruction {
  width: 100%;
  padding: 0 15px;
  font-size: 14px;
  font-style: italic;
  color: var(--body-text-color-secondary);
}

</style>
